import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import ReactPaginate from 'react-paginate'

import { PageLink, PageTitle } from '../../../../_metronic/layout/core'
import { Table } from '../../../../components/Table'
import { TableHead } from '../../../../components/Table/TableHead'
import { TableBody } from '../../../../components/Table/TableBody'
import { EPayoutTicketStatus } from '../../../../types/enums'
import { ROLE, useAuth } from '../../auth'
import { PAYOUT_REFUSALS_TAB } from '../refusals/core/consts'
import { IsGranted } from '../../auth/PrivateRoute'
import { getTicketsList } from '../api/getTicketsList'
import { IParamsTicketsListRequest } from '../api/ticketsList.types'
import { PayoutTicketListObject, PreparePayoutTicketsToObject } from '../core/_models'

import FinishedTicketItem from './FinishedTicketItem'
import { FilterManager, IBaseFilters } from '../../../common/filter/FilterManager'
import {
  ADAPTER_FILTER_LABEL,
  ADAPTER_ID_SEARCH_ERROR,
  adapterIdPayload,
  isAdapterScopeRequired,
  isAdapterSearchBlocked,
  usePaymentAdapterOptions,
} from '../../../common/filter/adapterIdSearch'
import { convertToUTCByMSK } from '../../../../utils'
import { getLast30DaysDateRange } from '../../../../utils/getLast30DaysDateRange'

import '../../../assets/css/pagination.css'

const accountBreadCrumbs: Array<PageLink> = [
  {
    title: 'Активные',
    path: '/payout-tickets/active',
    isSeparator: false,
    isActive: false,
    roles: [
      ROLE.ROLE_ADMIN,
      ROLE.ROLE_MANAGER,
      ROLE.ROLE_SENIOR_CASHIER,
      ROLE.ROLE_CASHIER,
      ROLE.ROLE_SENIOR_OPERATOR,
      ROLE.ROLE_OPERATOR,
    ],
  },
  {
    title: 'Завершённые',
    path: '/payout-tickets/finished',
    isSeparator: false,
    isActive: true,
    roles: [
      ROLE.ROLE_ADMIN,
      ROLE.ROLE_MANAGER,
      ROLE.ROLE_SENIOR_CASHIER,
      ROLE.ROLE_CASHIER,
      ROLE.ROLE_SENIOR_OPERATOR,
      ROLE.ROLE_OPERATOR,
    ],
  },
  {
    title: 'Отчёт',
    path: '/payout-tickets/report',
    isSeparator: false,
    isActive: false,
    roles: [
      ROLE.ROLE_ADMIN,
      ROLE.ROLE_MANAGER,
      ROLE.ROLE_SENIOR_CASHIER,
      ROLE.ROLE_SENIOR_OPERATOR,
    ],
  },
  PAYOUT_REFUSALS_TAB,
]

const perPage = 20
const defaultTicketList: PayoutTicketListObject = {}

const FinishedTickets = () => {
  const { currentUser } = useAuth()
  const [initialized, setInitialized] = useState(false)
  const { startDate, endDate } = getLast30DaysDateRange()
  const [filters, setFilters] = useState<IBaseFilters>({
      perPage: perPage,
      status: [
        EPayoutTicketStatus.COMPLETED,
        EPayoutTicketStatus.DECLINED,
      ],
      dateRange: { from: startDate, to: endDate },
    })
  const [tickets, setTickets] = useState(defaultTicketList)
  const [countPages, setCountPages] = useState(0)
  const [fullCount, setFullCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const adapterRequired = isAdapterScopeRequired(currentUser)
  const [searchError, setSearchError] = useState('')
  const adapterOptions = usePaymentAdapterOptions(adapterRequired)

  const getTicketList = useCallback(
    async (query: any) => {
      setLoading(true)
      try {
        const response = await getTicketsList(
          query, 
          currentUser
        )
        if (!response?.data) {
          throw new Error(
            'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error'
          )
        }
        setTickets({ ...PreparePayoutTicketsToObject(response.data.tickets || {}) })
        setCountPages(Math.ceil(response.data.totalRows / perPage))
        setFullCount(response.data.totalRows || 0)
      } catch (error) {
        console.error('Ошибка при загрузке тикетов:', error)
        setTickets({})
        setCountPages(0)
        setFullCount(0)
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const applyFilters = useCallback(
    (newFilters: IBaseFilters, pagination?: { page: number, perPage: number }) => {
      if (isAdapterSearchBlocked(adapterRequired, newFilters.searchAdvanced, 'paymentId', newFilters.paymentAdapterId)) {
        setSearchError(ADAPTER_ID_SEARCH_ERROR)
        return
      }
      setSearchError('')
      const query: Record<string, any> = {
        page: pagination?.page || 1,
        perPage: pagination?.perPage || perPage,
        status: newFilters.status,
      };

      if (newFilters.dateRange?.from) {
        query.createdDateStart = convertToUTCByMSK(newFilters.dateRange.from);
      }

      if (newFilters.dateRange?.to) {
        query.createdDateEnd = convertToUTCByMSK(newFilters.dateRange.to);
      }

      if (newFilters.searchAdvanced?.query?.trim() && newFilters.searchAdvanced.field) {
        query[newFilters.searchAdvanced.field] = newFilters.searchAdvanced.query;
      }
      Object.assign(query, adapterIdPayload(adapterRequired, newFilters.searchAdvanced, 'paymentId', newFilters.paymentAdapterId));

      getTicketList(query)
    },
    [getTicketList, adapterRequired]
  )

  useEffect(() => {
      if (!initialized) {
        applyFilters(filters, { page: 1, perPage })
        setInitialized(true)
      }
    }, [initialized, applyFilters, filters, perPage])

  const ticketsRender = useMemo(
    () => Object.keys(tickets).map((key) => <FinishedTicketItem ticket={tickets[key]} key={key} />),
    [tickets]
  )

  return (
    <div>
      <div className='card sticky-top-tickets mb-7'>
        <PageTitle breadcrumbs={accountBreadCrumbs}>Завершённые</PageTitle>
        <div className='card-header border-0 pt-5 pb-2'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Завершённые тикеты</span>
            <span className='text-muted mt-1 fw-semibold fs-7'>Количество: {fullCount}</span>
          </h3>
        </div>
        <FilterManager
          activeFilters={['dateRange', 'searchAdvanced', { filter: 'select', fieldName: 'paymentAdapterId', visibility: adapterRequired }]}
          onApplyFilters={applyFilters}
          filterProps={{
            dateRange: {
              ranges: ['currentShift', 'prevShift', '24hours', '7days', '30days'],
            },
            searchAdvanced: {
              options: [
                { label: 'ID тикета',     value: 'ticketToken' },
                { label: 'ID транзакции', value: 'merchantTransactionId' },
                { label: 'ID адаптера',   value: 'paymentId' },
              ],
              placeholder: 'Введите значение...',
              error: searchError,
            },
            paymentAdapterId: {
              label: ADAPTER_FILTER_LABEL,
              options: adapterOptions,
              placeholder: 'Не выбран',
            },
          }}
          defaultValues={{ ...filters, searchAdvanced: { query: '', field: 'ticketToken' } }}
        />
      </div>
      
      <div className='card'>
        <div className='card-body py-3'>
          <Table>
            <TableHead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th className='min-w-120px'>ID тикета</th>
                <th className='min-w-150px'>Дата создания</th>
                <th className='min-w-150px'>Тип</th>
                <th className='min-w-150px'>Дата закрытия</th>
                <th className='min-w-90px'>Фактическая Сумма</th>
                <th className='min-w-90px'>Запрошенная Сумма</th>
                <th className='min-w-120px'>Клиент</th>
                {!IsGranted([ROLE.ROLE_MATCHING_OPERATOR], currentUser) && (
                  <th className='min-w-100px'>Мерчант</th>
                )}
                <th className='min-w-80px'>ID адаптера</th>
                <th className='min-w-80px'>Адаптер завершения</th>
                <th className='min-w-120px'>Сотрудник</th>
                {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
                  <th className='min-w-100px'>Касса</th>
                )}
                <th className='min-w-100px'>Статус</th>
                <th className='min-w-50px'></th>
              </tr>
            </TableHead>
            <TableBody>
              {loading ? (
                <tr>
                  <td colSpan={IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) ? 16: 14}>Загрузка ...</td>
                </tr>
              ) : (
                ticketsRender
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <ReactPaginate
        activeClassName={'active-pagination '}
        breakClassName={'item-pagination break-me-pagination '}
        containerClassName={'pagination-pagination'}
        disabledClassName={'disabled-page-pagination'}
        nextClassName={'item-pagination ki-duotone ki-right fs-2x'}
        pageClassName={'item-pagination pagination-page-pagination '}
        previousClassName={'item-pagination ki-duotone ki-left fs-2x'}
        breakLabel='...'
        nextLabel=''
        onPageChange={(selected) =>
          applyFilters({ ...filters }, { page: selected.selected + 1, perPage })
        }
        pageRangeDisplayed={5}
        pageCount={countPages}
        previousLabel=''
        renderOnZeroPageCount={null}
      />
    </div>
  )
}

export default FinishedTickets
